/**
 * Time zones and wall clocks.
 *
 * Mass times on masstimes.org are local wall-clock times with no zone, a road
 * trip can cross zones, and Google stores the user's chosen departure as a
 * wall time too. Everything inside the planner is an absolute instant (epoch
 * milliseconds); this package is the one place wall times become instants and
 * back. It uses only `Intl`, so DST rules come from the runtime's tz database.
 */

/// <reference path="./tz-lookup.d.ts" />
import tzlookup from "tz-lookup";

export type WallTime = { year: number; month: number; day: number; hour: number; minute: number };

/** `YYYY-MM-DD` local calendar date. */
export type DateKey = string;

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** IANA zone at a coordinate, e.g. `America/Denver`. Falls back to UTC on bad input. */
export function timeZoneAt(lat: number, lng: number): string {
  try {
    return tzlookup(lat, lng);
  } catch {
    return "UTC";
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      weekday: "short",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The wall clock in `timeZone` at an instant, with its weekday (0 = Sunday). */
export function wallTimeAt(instant: number, timeZone: string): WallTime & { weekday: number } {
  const parts: Record<string, string> = {};
  for (const part of partsFormatter(timeZone).formatToParts(new Date(instant))) parts[part.type] = part.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAYS[parts.weekday ?? "Sun"] ?? 0,
  };
}

/** Minutes the zone is ahead of UTC at an instant (Denver in summer: -360). */
export function offsetMinutes(instant: number, timeZone: string): number {
  const wall = wallTimeAt(instant, timeZone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  return Math.round((asUtc - Math.floor(instant / MINUTE) * MINUTE) / MINUTE);
}

/**
 * The instant a wall clock in `timeZone` shows `wall`.
 *
 * In the spring-forward gap (a time that never happens) this lands an hour
 * later, which is what a clock that skips does; in the fall-back overlap it
 * picks the first occurrence.
 */
export function instantOf(wall: WallTime, timeZone: string): number {
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const first = asUtc - offsetMinutes(asUtc, timeZone) * MINUTE;
  const second = asUtc - offsetMinutes(first, timeZone) * MINUTE;
  if (first === second) return first;
  const shows = (instant: number) => {
    const at = wallTimeAt(instant, timeZone);
    return at.hour === wall.hour && at.minute === wall.minute && at.day === wall.day;
  };
  const valid = [first, second].filter(shows);
  // Overlap: both readings are valid, so take the earlier. Gap: neither is, so take the later.
  return valid.length > 0 ? Math.min(...valid) : Math.max(first, second);
}

const pad = (value: number) => String(value).padStart(2, "0");

export function dateKeyOf(wall: { year: number; month: number; day: number }): DateKey {
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`;
}

/** Local calendar date of an instant in a zone. */
export function dateKeyAt(instant: number, timeZone: string): DateKey {
  return dateKeyOf(wallTimeAt(instant, timeZone));
}

export function parseDateKey(key: DateKey): { year: number; month: number; day: number } {
  const [year, month, day] = key.split("-").map(Number) as [number, number, number];
  return { year, month, day };
}

/** Calendar arithmetic on date keys, independent of any zone. */
export function addDays(key: DateKey, days: number): DateKey {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return dateKeyOf({ year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() });
}

/** 0 = Sunday. */
export function weekdayOf(key: DateKey): number {
  const { year, month, day } = parseDateKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Every date from `first` to `last` inclusive. */
export function dateRange(first: DateKey, last: DateKey): DateKey[] {
  const keys: DateKey[] = [];
  for (let key = first; key <= last; key = addDays(key, 1)) keys.push(key);
  return keys;
}

/** "9:30 AM" (or "9:30 AM MDT" with `zoneName`) in the zone where it happens. */
export function formatClock(instant: number, timeZone: string, options: { zoneName?: boolean } = {}): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    ...(options.zoneName ? { timeZoneName: "short" } : {}),
  }).format(new Date(instant));
}

/** "Sun, Oct 18" in the zone where it happens. */
export function formatDay(instant: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }).format(
    new Date(instant),
  );
}

/** "1 h 25 min", "45 min". */
export function formatDuration(ms: number): string {
  const minutes = Math.round(Math.abs(ms) / MINUTE);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
